/**
 * The icon rail: where this app's few destinations live.
 *
 * SLIM, AND DELIBERATELY SO. t10's rail names forty destinations and expands into a text list;
 * this app has three, and a rail that expanded to show three words would be a wide empty column.
 * 64px of icons is the right size for the thing it is.
 *
 * SETTINGS IS GATED ON A PRIVILEGE, NOT ON BEING THE ADMINISTRATOR. The Administrator is an
 * ordinary role that holds every privilege by construction -- a bypass would make Effective
 * privileges and Granted-by lie -- so the rail asks for `document:configure`, which the
 * Administrator has and anybody else has only if it was granted to one of their roles.
 *
 * AND HIDING IT IS NOT THE CONTROL. The service refuses a write without the same privilege. This
 * only stops somebody being shown a door they cannot open.
 */
import React from "react";
import {
    IconButton, Tooltip } from "@mui/material";
// THE SAME DOCUMENTATION ICON THE REST OF THE SUITE USES. t1, t2 and t6 all mark it with
// HelpOutlineOutlined; this app had MenuBookOutlined, which reads as a different thing in a
// sidebar somebody meets in four products.
import { DescriptionOutlined, SettingsOutlined, HelpOutlineOutlined } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";

import css_buttons from "../Common/css/CCButton.module.scss";
import { hasPrivilege } from "../../util/SessionContext";
import { Grey2 } from "../../theme/styles";

const WIDTH = 64;
const ACTIVE_BG = "#E8F4FA";

type Destination = {
    label: string;
    path: string;
    icon: React.ReactNode;
    /** When set, the rail shows this only to somebody who holds it. */
    privilege?: string;
    /** About the app rather than a destination within it. The rail's single divider sits above
        the first of these. */
    trailing?: boolean;
};

// THE SUITE'S RAIL ICON SIZE, the same value t1, t5 and t10 pass to every icon in their nav
// models. Without it MUI renders at its own 1.5rem default, which reads larger than the same
// icon in the app next door.
const iconSx = {width: 22, height: 22};

const DESTINATIONS: Destination[] = [
    { label: "Documents", path: "/", icon: <DescriptionOutlined sx={iconSx}/> },
    // THE TRAILING GROUP, in the suite's order: Settings then Documentation.
    { label: "Settings", path: "/settings", icon: <SettingsOutlined sx={iconSx}/>,
      privilege: "document:configure", trailing: true },
    { label: "Documentation", path: "/help", icon: <HelpOutlineOutlined sx={iconSx}/>,
      trailing: true },
];

export default function Sidebar() {
    const navigate = useNavigate();
    const location = useLocation();

    const here = (path: string) =>
        path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

    return (
        <div style={{ width: WIDTH, flexShrink: 0, borderRight: `1px solid ${Grey2}`,
                      display: "flex", flexDirection: "column", alignItems: "center",
                      paddingTop: 8, gap: 4, backgroundColor: "#FFFFFF" }}>
            {DESTINATIONS.filter(one => !one.privilege || hasPrivilege(one.privilege))
                .map((one, index, shown) => (
                    <React.Fragment key={one.path}>
                    {/* THE RAIL'S ONE DIVIDER, above the trailing group, and the same line in
                        every app in the suite. Computed against what is SHOWN rather than against
                        the full list, so hiding Settings for somebody without the privilege does
                        not leave a divider floating above Documentation alone -- it moves to sit
                        above whichever trailing entry they can actually see. */}
                    {one.trailing && !shown[index - 1]?.trailing ? (
                        <div style={{ height: 1, alignSelf: "stretch", backgroundColor: Grey2,
                                      margin: "8px 12px" }}/>
                    ) : null}
                    <Tooltip title={one.label} placement="right">
                        {/* THE SUITE'S RAIL BUTTON, not one of this app's own. t5 and t10 both
                            carry css_buttons.blueIconButton here and change ONLY the background
                            when a destination is current; this app greyed the icon when it was not
                            and tinted it grey on hover, so the same rail looked like two different
                            controls depending on which product you were in. */}
                        <IconButton
                            className={css_buttons.blueIconButton}
                            aria-label={one.label}
                            aria-current={here(one.path) ? "page" : undefined}
                            onClick={() => navigate(one.path)}
                            style={{ width: 40, height: 40, flexShrink: 0,
                                     backgroundColor: here(one.path) ? ACTIVE_BG : undefined }}>
                            {one.icon}
                        </IconButton>
                    </Tooltip>
                    </React.Fragment>
                ))}
        </div>
    );
}
